import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { SKIN_ICON } from '@/components/AppShell';
import { edicionActiva } from '@/config/ediciones';
import { auth } from '@/lib/firebase';
import { CANONICAL_INDEX } from '@/config/eventos';
import {
  fetchInscritosFinde,
  flattenInscritos,
  groupByPrueba,
  InscritosNoPublicados,
  sortRows,
  type CompeticionInscritos,
  type InscritoRow,
  type InscritosFinde,
  type SortDir,
  type SortKey,
} from '@/lib/inscritosFinde';
import { normalizeNameForMatch, stripDiacritics } from '@/lib/normalize';

/**
 * Datos para el speaker — inscritos por prueba del campeonato y dossier del
 * comentarista. Portado de la vista "Inscritos" de la rama
 * feat/inscritos-licencias, adaptado al panel (estilos ld-*) y acotado a los
 * Ctos. de España sub-20 y Absoluto.
 *
 * Los datos vienen del JSON público de inscritos del fin de semana que
 * publica el motor cada jueves (src/config/inscritos.ts). Los botones de
 * informe llaman a la Cloud Function `informe` (/api/informe, Pillow):
 * funcionan también en PRO. En dev, el proxy de Vite apunta a la función
 * desplegada.
 */

type GeneroFiltro = 'M' | 'F';

/** Solo interesan el Cto. de España sub-20 y el Absoluto (decisión Santi
 *  2026-07-22). El JSON trae todas las competiciones del finde; el resto se
 *  descarta del desplegable. OJO: el Absoluto AL 2026 llega como "Campeonato
 *  de España" a secas (sin "Absoluto"), así que un "Campeonato de España" sin
 *  calificativo de categoría se trata como el Absoluto. */
function esCtoObjetivo(nombre: string): boolean {
  const n = stripDiacritics(nombre).toLowerCase().trim();
  if (!n.includes('campeonato de espana')) return false;
  if (/sub[\s-]?20/.test(n) || n.includes('absoluto')) return true;
  return !/sub[\s-]?\d+|master|veteran|promesa|juvenil|cadete|junior|clubes|federacion/.test(n);
}

function formatVentana(desde: string, hasta: string): string {
  const d = new Date(desde + 'T00:00:00');
  const h = new Date(hasta + 'T00:00:00');
  const dia = (x: Date) => x.toLocaleDateString('es-ES', { day: 'numeric' });
  const mes = h.toLocaleDateString('es-ES', { month: 'long' });
  return `${dia(d)}–${dia(h)} de ${mes}`;
}

export function Speaker() {
  const [data, setData] = useState<InscritosFinde | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [noPublicado, setNoPublicado] = useState(false);

  const [competicion, setCompeticion] = useState<string>('');
  const [genero, setGenero] = useState<GeneroFiltro>('M');
  const [busqueda, setBusqueda] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);

  // Generador de informe (solo local). Valor = `${pruebaLabel}|||${sexo}`.
  const [reportSel, setReportSel] = useState<string>('');
  const [busyModo, setBusyModo] = useState<'sencillo' | 'todas' | null>(null);
  const [reportError, setReportError] = useState<string | null>(null);

  useEffect(() => {
    const ac = new AbortController();
    setLoading(true);
    setError(null);
    setNoPublicado(false);
    fetchInscritosFinde(ac.signal)
      .then((d) => setData(d))
      .catch((e: unknown) => {
        if (ac.signal.aborted) return;
        if (e instanceof InscritosNoPublicados) setNoPublicado(true);
        else setError(e instanceof Error ? e.message : 'Error cargando inscritos.');
      })
      .finally(() => {
        if (!ac.signal.aborted) setLoading(false);
      });
    return () => ac.abort();
  }, []);

  // Ctos. del finde que nos interesan (sub-20 / Absoluto), por fecha.
  const ctos: CompeticionInscritos[] = useMemo(() => {
    if (!data) return [];
    return data.competiciones
      .filter((c) => esCtoObjetivo(c.nombre))
      .sort((a, b) => (a.fecha_inicio ?? '').localeCompare(b.fecha_inicio ?? ''));
  }, [data]);

  // Al cargar (o si el Cto. activo desaparece del JSON), selecciona el primero.
  useEffect(() => {
    if (!ctos.length) return;
    if (!competicion || !ctos.some((c) => c.nombre === competicion)) {
      setCompeticion(ctos[0].nombre);
    }
  }, [ctos, competicion]);

  const rows: InscritoRow[] = useMemo(() => (data ? flattenInscritos(data) : []), [data]);

  // Búsqueda por atleta dentro del Cto. (sin tildes, orden-insensible). Con
  // texto, ignora el filtro de género para que el atleta aparezca siempre.
  const qNorm = normalizeNameForMatch(busqueda);
  const buscando = qNorm.length >= 2;

  const grupos = useMemo(() => {
    const filtradas = rows.filter((r) => {
      if (r.competicion !== competicion) return false;
      if (buscando) return normalizeNameForMatch(r.atleta).includes(qNorm);
      return r.sexo === genero;
    });
    return groupByPrueba(filtradas);
  }, [rows, competicion, genero, buscando, qNorm]);

  const totalInscritos = useMemo(() => grupos.reduce((s, g) => s + g.count, 0), [grupos]);
  const ctoSel = ctos.find((c) => c.nombre === competicion) ?? null;

  // Pruebas del Cto. seleccionado (ambos sexos) para el desplegable del
  // informe, en el orden canónico del programa. Independiente del género.
  const reportPruebaItems = useMemo(() => {
    if (!competicion) return [];
    const map = new Map<string, { prueba: string; pruebaKey: string; sexo: string }>();
    for (const r of rows) {
      if (r.competicion !== competicion) continue;
      const value = `${r.prueba}|||${r.sexo}`;
      if (!map.has(value))
        map.set(value, { prueba: r.prueba, pruebaKey: r.pruebaKey, sexo: r.sexo });
    }
    const orden = (k: string) => CANONICAL_INDEX.get(k) ?? 999;
    return [...map.entries()]
      .sort(
        (a, b) =>
          orden(a[1].pruebaKey) - orden(b[1].pruebaKey) ||
          a[1].prueba.localeCompare(b[1].prueba, 'es') ||
          a[1].sexo.localeCompare(b[1].sexo),
      )
      .map(([value, { prueba, sexo }]) => ({
        value,
        label: `${prueba} ${sexo === 'M' ? 'H' : 'M'}`,
      }));
  }, [rows, competicion]);

  // Al cambiar de Cto., resetea la selección del informe.
  useEffect(() => {
    setReportSel('');
    setReportError(null);
  }, [competicion]);

  // Genera el PDF en la Cloud Function `informe` (rewrite /api/informe).
  // El completo (38 pruebas) tarda: la función hidrata los buckets de todos
  // los atletas del campeonato y pagina el PDF entero.
  const generarInforme = async (modo: 'sencillo' | 'todas') => {
    if (!competicion || busyModo) return;
    if (modo !== 'todas' && !reportSel) return;
    setBusyModo(modo);
    setReportError(null);
    try {
      const u = auth.currentUser;
      if (!u) throw new Error('Sin sesión activa');
      const idToken = await u.getIdToken();
      let url = `/api/informe?competicion=${encodeURIComponent(competicion)}&modo=${modo}`;
      if (modo !== 'todas') {
        const [prueba, sexo] = reportSel.split('|||');
        url += `&prueba=${encodeURIComponent(prueba)}&sexo=${encodeURIComponent(sexo)}`;
      }
      const res = await fetch(url, { headers: { Authorization: `Bearer ${idToken}` } });
      if (!res.ok) throw new Error((await res.text()) || `HTTP ${res.status}`);
      const blob = await res.blob();
      const cd = res.headers.get('Content-Disposition') ?? '';
      const fname = /filename="([^"]+)"/.exec(cd)?.[1] ?? 'informe.pdf';
      const dlUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = dlUrl;
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(dlUrl);
    } catch (e) {
      setReportError(e instanceof Error ? e.message : 'Error generando el informe.');
    } finally {
      setBusyModo(null);
    }
  };

  return (
    // Misma cabecera que /telegram (Mundo Atletismo, tema retro), no el
    // AppShell admin (decisión Santi 2026-07-22), con vuelta al panel.
    <div className="ld-shell skin-retro">
      <header className="ld-header">
        <span
          className="ld-header__brand"
          style={{ display: 'flex', alignItems: 'center', gap: 10 }}
        >
          <img className="ld-header__mark" src={SKIN_ICON.retro} alt="" width={38} height={38} />
          <span>Mundo Atletismo</span>
        </span>
        {/* Vuelta al panel, a la derecha como en /telegram (Santi 2026-07-22). */}
        <div className="ld-header__user">
          <Link
            to={`/${edicionActiva().id}`}
            className="ld-btn ld-btn--ghost ld-btn--small"
            style={{ textDecoration: 'none', whiteSpace: 'nowrap' }}
            aria-label="Volver al panel del campeonato"
          >
            ← Panel
          </Link>
        </div>
      </header>

      <main className="ld-main">
        <div className="ld-stack ld-stack--lg">
          <div className="ld-card">
            <p className="muted mono" style={{ margin: 0, fontSize: 11, letterSpacing: '0.06em' }}>
              HERRAMIENTA · SPEAKER
            </p>
            <h2 className="ld-card__title" style={{ margin: '4px 0 0' }}>
              Datos para el speaker
            </h2>
            <p className="muted" style={{ margin: '4px 0 0', fontSize: 13 }}>
              Inscritos por prueba del campeonato y dossier del comentarista.
              {data
                ? ` Fin de semana del ${formatVentana(data.ventana.desde, data.ventana.hasta)}.`
                : ''}
            </p>
          </div>

          {loading ? (
            <div className="ld-card">
              <p className="muted" style={{ margin: 0 }}>
                <span className="ld-spinner" aria-hidden /> Cargando inscritos del fin de semana…
              </p>
            </div>
          ) : error ? (
            <div className="ld-card">
              <p style={{ margin: 0, color: 'var(--ld-danger)' }}>{error}</p>
            </div>
          ) : !ctos.length ? (
            <div className="ld-card">
              <p className="muted" style={{ margin: 0 }}>
                {noPublicado
                  ? 'Aún no hay inscritos publicados para el próximo fin de semana. El motor los captura el jueves.'
                  : 'El JSON de inscritos de este fin de semana no incluye ningún Cto. de España sub-20 ni Absoluto.'}
              </p>
            </div>
          ) : (
            <>
              <div className="ld-card">
                <div className="ld-row" style={{ gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                  <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13 }}>
                    Campeonato:
                    <select value={competicion} onChange={(e) => setCompeticion(e.target.value)}>
                      {ctos.map((c) => (
                        <option key={c.id} value={c.nombre}>
                          {c.nombre}
                          {c.lugar ? ` · ${c.lugar}` : ''}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13 }}>
                    Género:
                    <select
                      value={genero}
                      onChange={(e) => setGenero(e.target.value as GeneroFiltro)}
                      disabled={buscando}
                    >
                      <option value="M">Hombres</option>
                      <option value="F">Mujeres</option>
                    </select>
                  </label>
                  <input
                    type="text"
                    value={busqueda}
                    onChange={(e) => setBusqueda(e.target.value)}
                    placeholder="Buscar atleta por nombre…"
                    aria-label="Buscar atleta inscrito"
                    style={{ flex: 1, minWidth: 200 }}
                  />
                </div>
                {ctoSel ? (
                  <p className="faint mono" style={{ margin: '8px 0 0', fontSize: 12 }}>
                    {ctoSel.lugar ?? '—'}
                    {ctoSel.fecha_inicio
                      ? ` · ${ctoSel.fecha_inicio}${ctoSel.fecha_fin && ctoSel.fecha_fin !== ctoSel.fecha_inicio ? ` a ${ctoSel.fecha_fin}` : ''}`
                      : ''}
                  </p>
                ) : null}
              </div>

              {competicion ? (
                <div className="ld-card">
                  <p
                    className="muted mono"
                    style={{ margin: '0 0 8px', fontSize: 11, letterSpacing: '0.06em' }}
                  >
                    GENERAR INFORME
                  </p>
                  <div
                    className="ld-row"
                    style={{ gap: 8, flexWrap: 'wrap', alignItems: 'center' }}
                  >
                    <select
                      value={reportSel}
                      onChange={(e) => setReportSel(e.target.value)}
                      aria-label="Prueba del informe"
                      style={{ minWidth: 180 }}
                    >
                      <option value="">Elige una prueba…</option>
                      {reportPruebaItems.map((it) => (
                        <option key={it.value} value={it.value}>
                          {it.label}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="ld-btn ld-btn--primary"
                      onClick={() => void generarInforme('sencillo')}
                      disabled={!reportSel || busyModo !== null}
                    >
                      {busyModo === 'sencillo' ? (
                        <>
                          <span className="ld-spinner" aria-hidden /> Generando…
                        </>
                      ) : (
                        'Informe prueba'
                      )}
                    </button>
                    <button
                      type="button"
                      className="ld-btn"
                      title="Todas las pruebas del campeonato (sin relevos) en un solo PDF"
                      onClick={() => void generarInforme('todas')}
                      disabled={busyModo !== null}
                    >
                      {busyModo === 'todas' ? (
                        <>
                          <span className="ld-spinner" aria-hidden /> Generando…
                        </>
                      ) : (
                        'Informe completo'
                      )}
                    </button>
                    {reportError ? (
                      <span style={{ color: 'var(--ld-danger)', fontSize: 12 }}>{reportError}</span>
                    ) : null}
                  </div>
                </div>
              ) : null}

              <div className="ld-card">
                <div className="ld-spread" style={{ marginBottom: 10 }}>
                  <h3 style={{ margin: 0, fontSize: 15 }}>Pruebas</h3>
                  <span className="muted mono" style={{ fontSize: 12 }}>
                    {grupos.length} pruebas · {totalInscritos} inscritos
                  </span>
                </div>
                {grupos.length === 0 ? (
                  <p className="muted" style={{ margin: 0, fontSize: 13 }}>
                    {buscando
                      ? `Ningún inscrito coincide con «${busqueda.trim()}».`
                      : 'No hay inscritos para esta combinación.'}
                  </p>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead>
                        <tr>
                          <th style={{ ...TH, width: 28 }} />
                          <th style={TH}>Prueba</th>
                          <th style={{ ...TH, textAlign: 'right' }}>Inscritos</th>
                        </tr>
                      </thead>
                      <tbody>
                        {grupos.map((g) => (
                          <FilaPrueba
                            key={g.key}
                            prueba={g.prueba}
                            sexo={g.sexo}
                            count={g.count}
                            rows={g.rows}
                            // al buscar, se abren todas las pruebas con coincidencias
                            expanded={buscando || expanded === g.key}
                            onToggle={() => setExpanded((cur) => (cur === g.key ? null : g.key))}
                          />
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}

const TH: CSSProperties = {
  textAlign: 'left',
  padding: '6px 10px',
  borderBottom: '1px solid var(--ld-line-strong)',
  color: 'var(--ld-text-muted)',
  fontSize: 11,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  fontWeight: 600,
  whiteSpace: 'nowrap',
};

const TD: CSSProperties = {
  padding: '6px 10px',
  borderBottom: '1px solid var(--ld-line)',
  verticalAlign: 'top',
};

const COLUMNAS: { key: SortKey; label: string; num?: boolean }[] = [
  { key: 'atleta', label: 'Atleta' },
  { key: 'club', label: 'Club' },
  { key: 'marca', label: 'Marca', num: true },
];

interface FilaPruebaProps {
  prueba: string;
  sexo: string;
  count: number;
  rows: InscritoRow[];
  expanded: boolean;
  onToggle: () => void;
}

function FilaPrueba({ prueba, sexo, count, rows, expanded, onToggle }: FilaPruebaProps) {
  // Por defecto, ordenado por MARCA ascendente = mejor marca primero (asc en
  // sortRows significa mejor: menor tiempo en carreras, mayor distancia en
  // concursos).
  const [sortKey, setSortKey] = useState<SortKey | null>('marca');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const clickCol = (key: SortKey) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const filas = useMemo(
    () => (sortKey ? sortRows(rows, prueba, sortKey, sortDir) : rows),
    [rows, prueba, sortKey, sortDir],
  );

  return (
    <>
      <tr style={{ cursor: 'pointer' }} onClick={onToggle}>
        <td style={{ ...TD, width: 28 }}>
          <span
            aria-hidden
            style={{
              display: 'inline-block',
              color: 'var(--ld-accent)',
              transform: expanded ? 'rotate(90deg)' : 'none',
              transition: 'transform 0.15s ease',
            }}
          >
            ▸
          </span>
        </td>
        <td style={{ ...TD, fontWeight: 600 }}>
          {prueba}
          {/* al buscar se mezclan sexos: la etiqueta desambigua */}
          <span className="faint" style={{ marginLeft: 6, fontWeight: 400 }}>
            {sexo === 'M' ? 'H' : sexo === 'F' ? 'M' : ''}
          </span>
        </td>
        <td style={{ ...TD, textAlign: 'right' }} className="mono">
          {count}
        </td>
      </tr>
      {expanded && (
        <tr>
          <td
            colSpan={3}
            style={{ padding: '4px 10px 14px 38px', borderBottom: '1px solid var(--ld-line)' }}
          >
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr>
                  {COLUMNAS.map((c) => {
                    const activa = sortKey === c.key;
                    return (
                      <th
                        key={c.key}
                        style={{
                          ...TH,
                          textAlign: c.num ? 'right' : 'left',
                          cursor: 'pointer',
                          userSelect: 'none',
                        }}
                        aria-sort={
                          activa ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'
                        }
                        onClick={() => clickCol(c.key)}
                        title={`Ordenar por ${c.label}`}
                      >
                        {c.label}
                        <span aria-hidden style={{ opacity: activa ? 0.9 : 0.25, marginLeft: 4 }}>
                          {activa ? (sortDir === 'asc' ? '▲' : '▼') : '⇅'}
                        </span>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {filas.map((r, idx) => (
                  <tr key={`${r.atleta}-${idx}`}>
                    <td style={TD}>{r.atleta}</td>
                    <td style={{ ...TD, color: 'var(--ld-text-muted)' }}>{r.club ?? '—'}</td>
                    <td style={{ ...TD, textAlign: 'right' }} className="mono">
                      {r.marca ?? '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </td>
        </tr>
      )}
    </>
  );
}
